import { type Theme, createTheme } from "@mui/material/styles";

export type ThemeMode = "light" | "dark";

/**
 * ЄДИНЕ джерело дизайн-токенів для бек-офісу.
 *
 * ПРАВИЛО: жоден компонент не має захардкоджувати кольори, spacing чи typography.
 * Усе читається з теми через `theme.palette.*`, `theme.spacing()` або через
 * shortcut-значення в `sx` (напр. `p: 2`, `color: "text.secondary"`).
 *
 * При додаванні нового токена — він має мати адекватне значення в обох режимах
 * (light і dark). Перевіряй обидва перед мерджем.
 */
export function createAppTheme(mode: ThemeMode): Theme {
  const isDark = mode === "dark";

  return createTheme({
    palette: {
      mode,
      primary: isDark
        ? {
            main: "#818cf8", // indigo-400 — світліший, щоб читатись на темному bg
            light: "#a5b4fc",
            dark: "#6366f1",
            contrastText: "#0f172a",
          }
        : {
            main: "#4f46e5",
            light: "#6366f1",
            dark: "#4338ca",
            contrastText: "#ffffff",
          },
      secondary: {
        main: isDark ? "#a5b4fc" : "#6366f1",
      },
      error: { main: isDark ? "#f87171" : "#e11d48" },
      warning: { main: isDark ? "#fbbf24" : "#d97706" },
      info: { main: isDark ? "#38bdf8" : "#0284c7" },
      success: { main: isDark ? "#34d399" : "#059669" },
      background: isDark
        ? {
            default: "#0f172a", // slate-900
            paper: "#1e293b", // slate-800
          }
        : {
            default: "#f8fafc",
            paper: "#ffffff",
          },
      text: isDark
        ? {
            primary: "#f1f5f9",
            secondary: "#94a3b8",
            disabled: "#64748b",
          }
        : {
            primary: "#0f172a",
            secondary: "#475569",
            disabled: "#94a3b8",
          },
      divider: isDark ? "rgba(148, 163, 184, 0.16)" : "#e2e8f0",
    },
    typography: {
      fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
      h1: { fontSize: "2rem", fontWeight: 600, letterSpacing: "-0.02em" },
      h2: { fontSize: "1.5rem", fontWeight: 600, letterSpacing: "-0.02em" },
      h3: { fontSize: "1.25rem", fontWeight: 600 },
      h4: { fontSize: "1.125rem", fontWeight: 600 },
      body1: { fontSize: "0.875rem", lineHeight: 1.5 },
      body2: { fontSize: "0.8125rem", lineHeight: 1.5 },
      button: { textTransform: "none", fontWeight: 500 },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ":root": {
            "--safe-top": "max(env(safe-area-inset-top), 0px)",
            "--safe-bottom": "max(env(safe-area-inset-bottom), 0px)",
            "--safe-left": "max(env(safe-area-inset-left), 0px)",
            "--safe-right": "max(env(safe-area-inset-right), 0px)",
          },
          body: {
            overscrollBehaviorY: "none",
            overflowX: "hidden",
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 8,
            paddingInline: 16,
            minHeight: 44,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            minWidth: 44,
            minHeight: 44,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 44,
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: ({ theme }) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 12,
            backgroundImage: "none",
          }),
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
        },
      },
      MuiTextField: {
        defaultProps: { size: "small", fullWidth: true },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "@media (pointer: coarse)": {
              minHeight: 44,
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            "@media (pointer: coarse)": {
              minHeight: 44,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: { fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase" },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundImage: "none",
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          }),
        },
      },
    },
  });
}

/** Legacy — для місць, які ще не мігрували на createAppTheme. */
export const theme = createAppTheme("light");
